import { capitalize } from "@sellar-io/common";
import { CardElement, PaymentElement } from "@stripe/react-stripe-js";
import { twMerge } from "tailwind-merge";
import amexSVG from "../../../../images/amex.svg";
import masterCardSVG from "../../../../images/mastercard.svg";
import visaSVG from "../../../../images/visa.svg";
import { SlideTransition } from "../../../../components/transition/SlideTransition";

type CardPayment = {
  type: "existing-card";
  paymentMethodId: string;
};

type NewCardPayment = {
  type: "new-card";
};

export type CardPaymentOptionsProps = {
  existingCardPaymentMethods: Array<{
    stripePaymentMethodId: string;
    brand: string;
    last4Digits: string;
  }>;
  activePayment: CardPayment | NewCardPayment;
  onSelectPaymentMethod: (payment: CardPayment | NewCardPayment) => void;
  useCardElement?: boolean;
  allowNewCardPayment?: boolean;
  showNewCardForm?: boolean;
};

export const CardPaymentOptions = ({
  existingCardPaymentMethods,
  activePayment,
  useCardElement,
  allowNewCardPayment = true,
  showNewCardForm = true,
  onSelectPaymentMethod,
}: CardPaymentOptionsProps) => {
  const isNewCardPaymentSelected = activePayment.type === "new-card";
  const selectedPaymentMethodId =
    "paymentMethodId" in activePayment ? activePayment.paymentMethodId : null;

  return (
    <div className="flex flex-1 flex-col">
      <span className="mb-2 font-semibold">Select payment card</span>
      <div className="flex flex-1 flex-col divide-y-2 divide-zinc-300 rounded-lg border border-zinc-300">
        {existingCardPaymentMethods.map((paymentMethod) => {
          const selectedExistingCardPayment =
            selectedPaymentMethodId === paymentMethod.stripePaymentMethodId;
          return (
            <PaymentMethodOption
              key={paymentMethod.stripePaymentMethodId}
              card={{
                last4: paymentMethod.last4Digits,
                logo: mapToCardSVG(paymentMethod.brand),
                label: paymentMethod.brand,
              }}
              selected={selectedExistingCardPayment}
              onClick={() => {
                onSelectPaymentMethod({
                  paymentMethodId: paymentMethod.stripePaymentMethodId,
                  type: "existing-card",
                });
              }}
            />
          );
        })}

        {allowNewCardPayment && (
          <div className="flex flex-1 flex-col">
            <button
              type="button"
              onClick={() => {
                onSelectPaymentMethod({
                  type: "new-card",
                });
              }}
              className={twMerge(
                "flex w-full flex-1 flex-col items-start rounded-b-lg bg-white p-4",
                isNewCardPaymentSelected && "rounded-none bg-gray-200",
              )}
              aria-expanded={isNewCardPaymentSelected}
            >
              <div className="flex w-full flex-1 items-center justify-between font-semibold">
                <div className="flex items-center gap-2">
                  <input
                    checked={isNewCardPaymentSelected}
                    readOnly
                    type="radio"
                    className="h-4 w-4 appearance-none rounded-full border-2 border-gray-800 text-black checked:border-[6px] checked:bg-white"
                  />
                  <span>Add a new payment card</span>
                </div>
                <div className="flex gap-2">
                  <img src={visaSVG} className="h-6" />
                  <img src={masterCardSVG} className="h-6" />
                  <img src={amexSVG} className="h-6" />
                </div>
              </div>
            </button>
            <SlideTransition
              show={isNewCardPaymentSelected}
              parentClass="duration-200 ease-out"
            >
              <div
                className={twMerge(
                  "flex w-full flex-1 flex-col rounded-b-lg bg-white px-4 pb-4",
                  isNewCardPaymentSelected && "bg-gray-200",
                )}
              >
                {isNewCardPaymentSelected &&
                  showNewCardForm &&
                  (useCardElement ? (
                    <CardElement options={{ hidePostalCode: true }} />
                  ) : (
                    <PaymentElement
                      options={{ paymentMethodOrder: ["card"] }}
                    />
                  ))}
              </div>
            </SlideTransition>
          </div>
        )}
      </div>
    </div>
  );
};

type PaymentMethodOptionProps = {
  card: {
    label: string;
    last4: string;
    logo: string;
  };
  selected: boolean;
  onClick: () => void;
};

const PaymentMethodOption = ({
  card,
  selected,
  onClick,
}: PaymentMethodOptionProps) => (
  <button
    type="button"
    onClick={onClick}
    className={twMerge(
      "flex flex-1 items-center justify-between bg-white p-4 first:rounded-t-lg last:rounded-b-lg",
      selected && "bg-gray-200",
    )}
  >
    <div className="flex gap-2">
      <input
        checked={selected}
        readOnly
        type="radio"
        className="h-4 w-4 appearance-none rounded-full border-2 border-gray-800 text-black checked:border-[6px] checked:bg-white"
      />
      <span className="font-semibold">{capitalize(card.label)}</span>
      <span className="text-zinc-400">Ending in {card.last4}</span>
    </div>
    <img src={card.logo} className="h-6 w-12" />
  </button>
);

const mapToCardSVG = (brand: string) => {
  switch (brand.toLowerCase()) {
    case "amex":
      return amexSVG;
    case "mastercard":
      return masterCardSVG;
    case "visa":
    case "diners":
    case "discover":
    case "eftpos_au":
    case "jcb":
    case "unionpay":
    case "unknown":
    default:
      return visaSVG;
  }
};
